import type {
  IPOI,
  IPoiCategory,
  IPoiPendingImage,
  IPoiTranslation,
  PersistedPoiDraft,
} from '@/entities/poi'
import type { ITenantDestination } from '@/entities/tenant-destination'
import type { PoiCategoryOption } from '@/features/poi/poi-basic-info-form'
import type { CreatePoiPayload, PoiType, UpdatePoiPayload } from '@/shared/api/types/poi-types'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'

import { ArrowLeft, Loader2, Plus, Save } from 'lucide-react'

import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import {
  buildPoiDraft,
  hasPoiDraftContent,
  useCreatePoiMutation,
  usePoiByIdQuery,
  useUpdatePoiMutation,
} from '@/entities/poi'
import { usePoiCategoriesInfiniteQuery, usePoiTypesQuery } from '@/entities/poi/api/poi-queries'
import { useTenantByIdQuery, useTenantLanguages } from '@/entities/tenant'
import {
  useTenantDestinationByIdQuery,
  useTenantDestinationsQuery,
} from '@/entities/tenant-destination'
import {
  useCreateTranslationDraft,
  useLocalizedTranslationJobController,
} from '@/features/localized-translation'
import { PoiBasicInfoForm } from '@/features/poi/poi-basic-info-form'
import { PoiImageUpload } from '@/features/poi/poi-image-upload'
import { PoiMapPicker } from '@/features/poi/poi-map-picker'
import { PoiTranslationSection } from '@/features/poi/poi-translation-section'
import { ReasonType, TRANSLATION_JOB_SCOPE } from '@/shared/constants'
import { DEFAULT_FETCH_LIMIT } from '@/shared/constants/pagination'
import { POI_TYPES } from '@/shared/constants/poi-type'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard'
import { uploadImageWithValidation } from '@/shared/lib/upload/upload-image-with-validation'
import { cn } from '@/shared/libs/cn'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { SectionHeader } from '@/shared/ui/section-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Skeleton } from '@/shared/ui/skeleton'
import { Switch } from '@/shared/ui/switch'
import {
  hasRequiredTranslations,
  mergeTranslationsForLanguages,
  resolveDefaultLanguages,
} from '@/shared/utils/language-utils'
import { resolvePoiCategoryName } from '@/shared/utils/poi-category-utils'

const DEFAULT_LOCATION = {
  latitude: 16.047079,
  longitude: 108.20623,
  range: 1,
  address: '',
}

const DEFAULT_BASIC_INFO: PoiBasicInfoState = {
  category: '',
  type: '',
  popupAd: false,
  range: 1,
}
export interface PoiBasicInfoState {
  category: string
  type: PoiType | ''
  popupAd: boolean
  range: number
}

type EditSection = 'translations' | 'location' | 'basicInfo' | 'media'

function successfulImageUrls(images: IPoiPendingImage[]): string[] {
  // * VI: Chỉ lấy URL đã upload thành công; bỏ item lỗi/chưa xong khi build payload. | EN: Keep uploaded URLs only; ignore errored/pending items when building payload.
  return images
    .filter((image) => image.status !== 'error' && image.url)
    .map((image) => image.url as string)
}

function createEmptyPoiTranslation(language: string): IPoiTranslation {
  return {
    language,
    name: '',
    description: '',
    audioContent: '',
    hasAudio: false,
  }
}

function buildPoiTranslationSnapshot(translations: IPoiTranslation[]): string {
  // * VI: Chuẩn hóa dữ liệu name/description/audio content cần so sánh để check unsaved changes. | EN: Serialize comparable name/description/audio fields for unsaved-change detection.
  return JSON.stringify(
    translations.map((item) => ({
      language: item.language,
      name: item.name.trim(),
      description: item.description.trim(),
      audioContent: item.audioContent?.trim() ?? '',
      hasAudio: Boolean(item.hasAudio),
      audioUrl: item.audioUrl ?? '',
    })),
  )
}

function buildLocationSnapshot(value: typeof DEFAULT_LOCATION): string {
  // * VI: Chuẩn hóa location thành JSON ổn định để so sánh dirty state. | EN: Normalize location into a stable JSON string for dirty-checking.
  return JSON.stringify({
    latitude: value.latitude,
    longitude: value.longitude,
    range: value.range,
    address: value.address?.trim() ?? '',
  })
}

function buildBasicInfoSnapshot(value: PoiBasicInfoState, tenantDestinationId: string): string {
  // * VI: Snapshot gồm cả tenant_destination_id để đổi destination cũng được tính là dirty. | EN: Include tenant destination binding in snapshot so destination changes mark the form dirty.
  return JSON.stringify({
    category: value.category,
    type: value.type,
    popupAd: value.popupAd,
    range: value.range,
    tenantDestinationId,
  })
}

function buildMediaSnapshot(urls: string[]): string {
  // * VI: Media so sánh theo URL, không so sánh object File tạm thời. | EN: Media dirty-check is URL-based; transient File objects are not compared directly.
  return JSON.stringify(urls)
}

export function PoiFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const params = useParams({ strict: false })
  const search: { lang?: unknown } = useSearch({ strict: false })
  const tenantId = params.tenantId ?? ''
  const requestedLanguageCode = typeof search.lang === 'string' ? search.lang.trim() : ''
  const { poiId } = params
  const isEdit = Boolean(poiId)

  const { data: tenantResponse } = useTenantByIdQuery(tenantId)
  const tenantLanguagesQuery = useTenantLanguages(tenantId)
  const tenantLanguageItems = tenantLanguagesQuery.items
  const supportedLanguages = useMemo(
    () => resolveDefaultLanguages(tenantLanguageItems),
    [tenantLanguageItems],
  )
  const tenantDefaultLanguage = useMemo(() => {
    if (!tenantLanguageItems.length) {
      return ''
    }
    return (
      tenantLanguageItems.find((item) => item.isDefault)?.languageCode ??
      supportedLanguages[0] ??
      ''
    )
  }, [supportedLanguages, tenantLanguageItems])
  const formLanguageCode = useMemo(() => {
    if (requestedLanguageCode && supportedLanguages.includes(requestedLanguageCode)) {
      return requestedLanguageCode
    }
    return tenantDefaultLanguage
  }, [requestedLanguageCode, supportedLanguages, tenantDefaultLanguage])

  const { data: poiResponse, isLoading: isPoiLoading } = usePoiByIdQuery(poiId ?? '')
  const isFormReady = !isPoiLoading && tenantLanguagesQuery.isFetched
  const { data: tenantDestinationsResponse, isLoading: isTenantDestinationsLoading } =
    useTenantDestinationsQuery({
      current: 1,
      pageSize: DEFAULT_FETCH_LIMIT,
      isActive: true,
      ...(formLanguageCode ? { languageCode: formLanguageCode } : {}),
    })

  const createMutation = useCreatePoiMutation()
  const updateMutation = useUpdatePoiMutation()

  const [basicInfo, setBasicInfo] = useState<PoiBasicInfoState>({
    category: '',
    type: POI_TYPES.MAJOR,
    popupAd: false,
    range: 1,
  })
  const [location, setLocation] = useState(DEFAULT_LOCATION)
  const [translations, setTranslations] = useState<IPoiTranslation[]>([])
  const [images, setImages] = useState<IPoiPendingImage[]>([])
  const [selectedTenantDestinationId, setSelectedTenantDestinationId] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [savingSection, setSavingSection] = useState<EditSection | null>(null)
  const [, setHasPendingTranslationGeneration] = useState(false)
  const draftEntityId = useMemo(() => `draft:poi:${tenantId || 'unknown-tenant'}`, [tenantId])
  const poiGlobalCategoriesQuery = usePoiCategoriesInfiniteQuery(tenantId, {
    ...(tenantDefaultLanguage ? { languageCode: tenantDefaultLanguage } : {}),
    pageSize: 10,
  })
  const poiTypesQuery = usePoiTypesQuery(formLanguageCode || undefined)

  const handleBasicInfoChange = (nextValue: PoiBasicInfoState) => {
    setBasicInfo(nextValue)
    setLocation((current) => ({
      ...current,
      range: nextValue.range,
    }))
  }

  const handleLocationChange = (nextValue: typeof DEFAULT_LOCATION) => {
    setLocation(nextValue)
    setBasicInfo((current) => ({
      ...current,
      range: nextValue.range,
    }))
  }

  const tenant = tenantResponse?.data
  const tenantDestinations = tenantDestinationsResponse?.data?.data ?? []
  const selectedTenantDestinationFromList = tenantDestinations.find(
    (item: ITenantDestination) => item.id === selectedTenantDestinationId,
  )
  const { data: selectedTenantDestinationResponse } = useTenantDestinationByIdQuery(
    selectedTenantDestinationId,
    formLanguageCode || undefined,
    { enabled: !!formLanguageCode },
  )
  const selectedTenantDestination =
    selectedTenantDestinationResponse?.data ?? selectedTenantDestinationFromList
  const selectedTenantDestinationCenter = useMemo(() => {
    const latitude = selectedTenantDestination?.centerLat
    const longitude = selectedTenantDestination?.centerLng
    if (typeof latitude !== 'number' || !Number.isFinite(latitude)) {
      return null
    }
    if (typeof longitude !== 'number' || !Number.isFinite(longitude)) {
      return null
    }
    return { latitude, longitude }
  }, [selectedTenantDestination?.centerLat, selectedTenantDestination?.centerLng])

  const bannerUrls = useMemo(() => successfulImageUrls(images), [images])
  const basicInfoCategoryOptions = useMemo<PoiCategoryOption[]>(() => {
    const optionMap = new Map<string, PoiCategoryOption>()

    const addOption = (item: IPoiCategory) => {
      const value = item.id.trim()
      if (!value) {
        return
      }
      const label = resolvePoiCategoryName(
        item,
        [formLanguageCode, tenantDefaultLanguage].filter(Boolean),
      )
      const current = optionMap.get(value)
      if (!current) {
        optionMap.set(value, {
          value,
          label: label || value,
          ...(item.icon ? { icon: item.icon } : {}),
        })
      }
    }

    for (const item of poiGlobalCategoriesQuery.data?.pages.flatMap((p) => p.data) ?? []) {
      addOption(item)
    }

    return Array.from(optionMap.values())
  }, [formLanguageCode, poiGlobalCategoriesQuery.data?.pages, tenantDefaultLanguage])

  const basicInfoTypeOptions = useMemo(() => {
    // * VI: Chuẩn hóa danh sách type về 2 loại MAJOR/MINOR, bỏ mọi giá trị legacy từ API. | EN: Normalize type options to MAJOR/MINOR only and ignore legacy values from API.
    const options = new Set<PoiType>()
    for (const item of poiTypesQuery.data?.data ?? []) {
      const value = item.trim().toLowerCase()
      if (value === POI_TYPES.MAJOR || value === POI_TYPES.MINOR) {
        options.add(value)
      }
    }
    if (basicInfo.type) {
      options.add(basicInfo.type)
    }
    if (options.size < 1) {
      return [POI_TYPES.MAJOR, POI_TYPES.MINOR]
    }
    return Array.from(options).sort((a, b) =>
      a === POI_TYPES.MAJOR ? -1 : b === POI_TYPES.MAJOR ? 1 : 0,
    )
  }, [basicInfo.type, poiTypesQuery.data?.data])

  // * VI: Snapshot gốc của từng section để so sánh dirty khi edit.
  const editHydrationSignatureRef = useRef<string | null>(null)
  const initialTranslationSnapshotRef = useRef('')
  const initialLocationSnapshotRef = useRef(buildLocationSnapshot(DEFAULT_LOCATION))
  const initialBasicInfoSnapshotRef = useRef(buildBasicInfoSnapshot(DEFAULT_BASIC_INFO, ''))
  const initialMediaSnapshotRef = useRef(buildMediaSnapshot([]))

  // * VI: Entity id dùng chung cho jobs + draft; edit dùng poiId, create dùng draft key theo tenant.
  const translationEntityId = isEdit && poiId ? poiId : draftEntityId
  const poiDraft = useMemo(
    () =>
      buildPoiDraft({
        basicInfo,
        location,
        translations,
        images,
        selectedTenantDestinationId,
      }),
    [basicInfo, images, location, selectedTenantDestinationId, translations],
  )
  const hasPoiDraft = useMemo(
    () =>
      hasPoiDraftContent(
        {
          basicInfo,
          location,
          translations,
          images,
          selectedTenantDestinationId,
        },
        {
          basicInfo: DEFAULT_BASIC_INFO,
          location: DEFAULT_LOCATION,
        },
      ),
    [basicInfo, images, location, selectedTenantDestinationId, translations],
  )
  const hydratePoiDraft = useCallback((persistedDraft?: PersistedPoiDraft) => {
    startTransition(() => {
      setBasicInfo(persistedDraft?.basicInfo ?? DEFAULT_BASIC_INFO)
      setLocation(persistedDraft?.location ?? DEFAULT_LOCATION)
      setTranslations(persistedDraft?.translations ?? [])
      setImages(persistedDraft?.images ?? [])
      setSelectedTenantDestinationId(persistedDraft?.selectedTenantDestinationId ?? '')
    })
    initialTranslationSnapshotRef.current = buildPoiTranslationSnapshot([])
    initialLocationSnapshotRef.current = buildLocationSnapshot(DEFAULT_LOCATION)
    initialBasicInfoSnapshotRef.current = buildBasicInfoSnapshot(DEFAULT_BASIC_INFO, '')
    initialMediaSnapshotRef.current = buildMediaSnapshot([])
  }, [])
  // * VI: Dùng cùng entity key với job background để draft và job đồng bộ theo từng content.
  const { pendingSuccessJobs } = useLocalizedTranslationJobController({
    scope: TRANSLATION_JOB_SCOPE.POI,
    entityId: translationEntityId,
    audioEnabled: ReasonType.TRANSLATE_AUDIO_AVAILABLE,
  })
  const { clearCurrentJobs } = useCreateTranslationDraft({
    enabled: !isEdit,
    scope: TRANSLATION_JOB_SCOPE.POI,
    entityId: translationEntityId,
    audioEnabled: ReasonType.TRANSLATE_AUDIO_AVAILABLE,
    initSignature: `create:${tenantId}`,
    draft: poiDraft,
    hasContent: hasPoiDraft,
    onHydrate: hydratePoiDraft,
  })

  useLayoutEffect(() => {
    // * VI: useLayoutEffect để hydrate form trước khi paint, tránh flash frame trống khi POI data đã có trong cache.
    if (!isEdit) {
      return
    }
    // * VI: Guard cho edit mode: khi route chưa có `poiId` hợp lệ (điều hướng chuyển trang hoặc params chưa sẵn sàng),
    // * VI: tuyệt đối không được persist draft vì sẽ ghi đè vào key không đúng entity.
    // * VI: Tắt cờ persist và thoát sớm để chờ `poiId` ổn định rồi mới hydrate/persist lại.

    if (!poiId) return
    if (!poiResponse?.data) return
    const poi = poiResponse.data
    // * VI: Signature gồm cả dữ liệu API để khi React Query refetch được POI mới, form hydrate lại thay vì giữ state cũ.
    const apiSnapshot = JSON.stringify({
      localizedData: poi.localizedData,
      category: poi.categoryData?.id ?? poi.category,
      type: poi.type,
      popupAd: poi.popupAd,
      range: poi.range,
      latitude: poi.latitude,
      longitude: poi.longitude,
      address: poi.address ?? '',
      banner: poi.banner,
      tenantDestinationId: poi.tenantDestinationId ?? '',
    })
    // * VI: supportedLanguages nằm trong signature để khi tenant languages về sau POI data, form hydrate lại và bổ sung tab còn thiếu.
    // * EN: supportedLanguages is part of the signature so the form re-hydrates once tenant languages arrive after the POI payload.
    const signature = `edit:${poiId}:${formLanguageCode || 'default'}:${supportedLanguages.join(',')}:${apiSnapshot}`
    // * VI: Signature chặn hydrate lặp; nhưng vẫn hydrate lại khi draft hoặc dữ liệu API thay đổi.
    if (editHydrationSignatureRef.current === signature) return

    editHydrationSignatureRef.current = signature
    const nextBasicInfo: PoiBasicInfoState = {
      category: poi.categoryData?.id ?? poi.category,
      type: poi.type,
      popupAd: poi.popupAd,
      range: poi.range,
    }
    const nextLocation = {
      latitude: poi.latitude,
      longitude: poi.longitude,
      range: poi.range,
      address: poi.address ?? '',
    }
    // * VI: POI chỉ có localizedData cho ngôn ngữ đã dịch; merge với ngôn ngữ tenant để ngôn ngữ thêm sau vẫn có tab trống để nhập.
    // * EN: A POI only carries localizedData for languages already translated; merge with the tenant list so languages added later still get an empty tab.
    const nextTranslations = mergeTranslationsForLanguages(
      supportedLanguages,
      poi.localizedData,
      createEmptyPoiTranslation,
    )
    const nextImages = poi.banner.map((url) => ({
      url,
      previewUrl: url,
      status: 'success' as const,
    }))
    const nextTenantDestinationId = poi.tenantDestinationId ?? ''
    const restoredBasicInfo = nextBasicInfo
    const restoredLocation = nextLocation
    const restoredTranslations = nextTranslations
    const restoredImages = nextImages
    const restoredTenantDestinationId = nextTenantDestinationId

    // * VI: Edit mode luôn hydrate từ API để draft/default cũ không đè dữ liệu POI sau refetch.
    // * EN: Edit mode always hydrates from the API so stale drafts/defaults cannot override refetched POI data.
    startTransition(() => {
      setBasicInfo(restoredBasicInfo)
      setLocation(restoredLocation)
      setTranslations(restoredTranslations)
      setImages(restoredImages)
      setSelectedTenantDestinationId(restoredTenantDestinationId)
    })
    // * VI: Snapshot lấy từ danh sách đã merge, nếu không các tab ngôn ngữ mới thêm sẽ bị coi là "thay đổi chưa lưu" ngay khi mở form.
    // * EN: Snapshot the merged list; otherwise the newly added language tabs read as unsaved changes the moment the form opens.
    initialTranslationSnapshotRef.current = buildPoiTranslationSnapshot(restoredTranslations)
    initialLocationSnapshotRef.current = buildLocationSnapshot({
      latitude: poi.latitude,
      longitude: poi.longitude,
      range: poi.range,
      address: poi.address ?? '',
    })
    initialBasicInfoSnapshotRef.current = buildBasicInfoSnapshot(
      {
        category: poi.categoryData?.id ?? poi.category,
        type: poi.type,
        popupAd: poi.popupAd,
        range: poi.range,
      },
      nextTenantDestinationId,
    )
    initialMediaSnapshotRef.current = buildMediaSnapshot(poi.banner)
  }, [formLanguageCode, isEdit, poiId, poiResponse?.data, supportedLanguages])

  useEffect(() => {
    if (isEdit || translations.length > 0) {
      return
    }
    if (supportedLanguages.length < 1) {
      return
    }
    const defaultTranslations = supportedLanguages.map(createEmptyPoiTranslation)
    setTranslations(defaultTranslations)
    initialTranslationSnapshotRef.current = buildPoiTranslationSnapshot(defaultTranslations)
  }, [isEdit, supportedLanguages, translations.length])

  const hasUploadingImages = images.some((image) => image.status === 'uploading')
  const hasErroredImages = images.some((image) => image.status === 'error')
  const isSaving = createMutation.isPending || updateMutation.isPending || hasUploadingImages
  const translationSnapshot = useMemo(
    () => buildPoiTranslationSnapshot(translations),
    [translations],
  )
  const locationSnapshot = useMemo(() => buildLocationSnapshot(location), [location])
  const basicInfoSnapshot = useMemo(
    () => buildBasicInfoSnapshot(basicInfo, selectedTenantDestinationId),
    [basicInfo, selectedTenantDestinationId],
  )
  const mediaSnapshot = useMemo(() => buildMediaSnapshot(bannerUrls), [bannerUrls])
  const hasCreateTranslationContent = useMemo(
    () =>
      translations.some((item) => {
        const hasText = item.name.trim().length > 0 || item.description.trim().length > 0
        const hasAudioFlag = item.hasAudio === true
        const hasAudioUrl = Boolean(item.audioUrl)
        return hasText || hasAudioFlag || hasAudioUrl
      }),
    [translations],
  )

  const isTranslationDirty = isEdit
    ? translationSnapshot !== initialTranslationSnapshotRef.current
    : hasCreateTranslationContent
  const isLocationDirty = isEdit
    ? locationSnapshot !== initialLocationSnapshotRef.current
    : locationSnapshot !== buildLocationSnapshot(DEFAULT_LOCATION)
  const isBasicInfoDirty = isEdit
    ? basicInfoSnapshot !== initialBasicInfoSnapshotRef.current
    : basicInfoSnapshot !== buildBasicInfoSnapshot(DEFAULT_BASIC_INFO, '')
  const isMediaDirty = isEdit
    ? mediaSnapshot !== initialMediaSnapshotRef.current
    : images.length > 0
  const isStatusDirty = !isEdit && !isActive
  const hasUnsavedChanges =
    isTranslationDirty || isLocationDirty || isBasicInfoDirty || isMediaDirty || isStatusDirty
  const { dialog: unsavedChangesDialog, allowNextNavigation } =
    useUnsavedChangesGuard(hasUnsavedChanges)

  function handleCreateCategoryNavigation() {
    const returnTo =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : scopedAdminPath(generatePath('/tenant/:tenantId/pois/create', { tenantId }))
    const createCategoryPath = `${scopedAdminPath(generatePath('/tenant/:tenantId/poi-categories/create', { tenantId }))}?returnTo=${encodeURIComponent(returnTo)}`
    allowNextNavigation()
    void navigate({ to: createCategoryPath as never })
  }

  function handleCreateDestinationNavigation() {
    const returnTo =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : scopedAdminPath(generatePath('/tenant/:tenantId/pois/create', { tenantId }))
    const createDestinationPath = `${scopedAdminPath(generatePath('/tenant/:tenantId/destinations', { tenantId }))}?returnTo=${encodeURIComponent(returnTo)}`
    allowNextNavigation()
    void navigate({ to: createDestinationPath as never })
  }

  async function handleSelectedImages(files: File[]) {
    for (const file of files) {
      const previewUrl = URL.createObjectURL(file)
      const localId = `${file.name}-${file.lastModified}-${Math.random().toString(16).slice(2)}`
      const pendingImage: IPoiPendingImage = {
        id: localId,
        file,
        previewUrl,
        status: 'uploading',
      }

      setImages((prev) => [...prev, pendingImage])

      try {
        const uploaded = await uploadImageWithValidation({ file })
        setImages((prev) =>
          prev.map((image) => {
            if (image.id !== localId) {
              return image
            }
            if (image.previewUrl.startsWith('blob:')) {
              URL.revokeObjectURL(image.previewUrl)
            }

            return {
              ...image,
              status: 'success',
              url: uploaded.url,
              blobName: uploaded.blobName,
              previewUrl: uploaded.url,
            }
          }),
        )
      } catch (error) {
        const message = getApiErrorText(error) ?? t('poi.media.imageUploadFailed')
        setImages((prev) =>
          prev.map((image) => {
            if (image.id !== localId) {
              return image
            }

            return {
              ...image,
              status: 'error',
              error: message,
            }
          }),
        )
      }
    }
  }

  async function handleSubmit() {
    // * VI: Submit full flow: validate đủ các section trước khi gửi payload cuối. | EN: Full submit flow: validate all sections before sending final payload.
    if (hasUploadingImages) {
      showToast.error(t('poi.media.waitForUpload'))
      return
    }

    if (hasErroredImages) {
      showToast.error(t('poi.media.resolveUploadErrors'))
      return
    }

    if (!selectedTenantDestinationId) {
      showToast.error(t('poi.destinationRequired'))
      return
    }
    const normalizedType = basicInfo.type
    if (!normalizedType) {
      showToast.error(t('poi.selectType'))
      return
    }
    const normalizedCategory = basicInfo.category.trim()
    if (!normalizedCategory) {
      showToast.error(t('poi.categoryRequired'))
      return
    }

    if (bannerUrls.length < 1) {
      showToast.error(t('poi.imagesRequired'))
      return
    }

    const normalizedTranslations = translations.map((item) => ({
      ...item,
      name: item.name.trim(),
      description: item.description.trim(),
      audioContent: item.audioContent.trim(),
      hasAudio: item.hasAudio as boolean,
    }))
    if (!hasRequiredTranslations(normalizedTranslations, ['name', 'description', 'audioContent'])) {
      showToast.error(t('poi.allLanguagesTranslationRequired'))
      return
    }

    const normalizedBanner = bannerUrls
    const normalizedRange = location.range

    const createPayload: CreatePoiPayload = {
      tenantId,
      tenantDestinationId: selectedTenantDestinationId,
      localizedData: normalizedTranslations,
      latitude: location.latitude,
      longitude: location.longitude,
      type: normalizedType,
      range: normalizedRange,
      ...(location.address?.trim() ? { address: location.address.trim() } : {}),
      popupAd: basicInfo.popupAd,
      ...(normalizedCategory ? { category: normalizedCategory } : {}),
      banner: normalizedBanner,
      audio: [],
      isActive,
    }

    const updatePayload: UpdatePoiPayload = {
      tenantDestinationId: selectedTenantDestinationId,
      localizedData: normalizedTranslations,
      latitude: location.latitude,
      longitude: location.longitude,
      type: normalizedType,
      range: normalizedRange,
      ...(location.address?.trim() ? { address: location.address.trim() } : {}),
      popupAd: basicInfo.popupAd,
      category: normalizedCategory,
      banner: normalizedBanner,
    }

    try {
      if (isEdit && poiId) {
        await updateMutation.mutateAsync({ id: poiId, payload: updatePayload })
        // * VI: Lưu thành công thì xóa draft để lần mở sau không restore dữ liệu cũ.
        // * EN: Clear draft after successful save so stale values are not restored next time.
        clearCurrentJobs()
        showToast.success(t('poi.updateSuccess'))
        allowNextNavigation()
        await navigate({
          to: scopedAdminPath(generatePath('/tenant/:tenantId/pois', { tenantId })) as never,
        })
      } else {
        await createMutation.mutateAsync(createPayload)
        // * VI: Tương tự create thành công thì clear draft.
        // * EN: Same rule for create success: clear draft after completion.
        clearCurrentJobs()
        showToast.success(t('poi.createSuccess'))
        allowNextNavigation()
        await navigate({
          to: scopedAdminPath(generatePath('/tenant/:tenantId/pois', { tenantId })) as never,
        })
      }
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  function buildNormalizedTranslations() {
    return translations.map((item) => ({
      ...item,
      name: item.name.trim(),
      description: item.description.trim(),
      audioContent: item.audioContent.trim(),
      hasAudio: item.hasAudio as boolean,
    }))
  }

  async function saveEditSection(
    section: EditSection,
    payload: UpdatePoiPayload,
  ): Promise<IPOI | null> {
    // * VI: Save theo section trong edit mode để patch từng khối, không cần submit toàn bộ form. | EN: Section save in edit mode patches one block without forcing full-form submit.
    if (!poiId) return null
    try {
      setSavingSection(section)
      const response = await updateMutation.mutateAsync({ id: poiId, payload })
      showToast.success(t('poi.updateSuccess'))
      return response.data
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
      return null
    } finally {
      setSavingSection(null)
    }
  }

  async function handleSaveTranslations() {
    const normalizedTranslations = buildNormalizedTranslations()
    if (!hasRequiredTranslations(normalizedTranslations, ['name', 'description', 'audioContent'])) {
      showToast.error(t('poi.allLanguagesTranslationRequired'))
      return
    }
    const updatedPoi = await saveEditSection('translations', {
      localizedData: normalizedTranslations,
    })
    if (updatedPoi) {
      setTranslations(updatedPoi.localizedData)
      initialTranslationSnapshotRef.current = buildPoiTranslationSnapshot(updatedPoi.localizedData)
      setHasPendingTranslationGeneration(false)
    }
  }

  async function handleSaveLocation() {
    const success = await saveEditSection('location', {
      latitude: location.latitude,
      longitude: location.longitude,
      range: location.range,
      ...(location.address?.trim() ? { address: location.address.trim() } : {}),
    })
    if (success) {
      initialLocationSnapshotRef.current = locationSnapshot
    }
  }

  async function handleSaveBasicInfo() {
    if (!selectedTenantDestinationId) {
      showToast.error(t('poi.destinationRequired'))
      return
    }
    const normalizedType = basicInfo.type
    if (!normalizedType) {
      showToast.error(t('poi.selectType'))
      return
    }
    const normalizedCategory = basicInfo.category.trim()
    if (!normalizedCategory) {
      showToast.error(t('poi.categoryRequired'))
      return
    }
    const success = await saveEditSection('basicInfo', {
      tenantDestinationId: selectedTenantDestinationId,
      type: normalizedType,
      popupAd: basicInfo.popupAd,
      category: normalizedCategory,
      range: basicInfo.range,
    })
    if (success) {
      initialBasicInfoSnapshotRef.current = basicInfoSnapshot
    }
  }

  async function handleSaveMedia() {
    if (hasUploadingImages) {
      showToast.error(t('poi.media.waitForUpload'))
      return
    }
    if (hasErroredImages) {
      showToast.error(t('poi.media.resolveUploadErrors'))
      return
    }
    if (successfulImageUrls(images).length < 1) {
      showToast.error(t('poi.imagesRequired'))
      return
    }

    const success = await saveEditSection('media', { banner: successfulImageUrls(images) })
    if (success) {
      initialMediaSnapshotRef.current = mediaSnapshot
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title={isEdit ? t('poi.edit') : t('poi.create')}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          {
            label: tenant?.name ?? tenantId,
            path: scopedAdminPath(generatePath('/tenant/:tenantId', { tenantId })),
          },
          {
            label: t('poi.title'),
            path: scopedAdminPath(generatePath('/tenant/:tenantId/pois', { tenantId })),
          },
          { label: isEdit ? t('poi.edit') : t('poi.create') },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => {
                void navigate({
                  to: scopedAdminPath(
                    generatePath('/tenant/:tenantId/pois', { tenantId }),
                  ) as never,
                })
              }}
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {t('common.back')}
            </Button>
            {!isEdit && (
              <Button disabled={isSaving || !hasUnsavedChanges} onClick={() => void handleSubmit()}>
                {isSaving ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-1.5 h-4 w-4" />
                )}
                {t('common.save')}
              </Button>
            )}
          </div>
        }
      />

      <div className="space-y-6">
        {/* Translations */}
        <div className="bg-white rounded-2xl border border-zinc-200 p-6 lg:p-7">
          <div className="mb-4 flex items-center justify-between gap-3">
            <SectionHeader title={t('poi.translations')} />
            {isEdit && (
              <Button
                size="sm"
                disabled={updateMutation.isPending || savingSection !== null}
                onClick={handleSaveTranslations}
              >
                {savingSection === 'translations' && (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                )}
                {t('common.save')}
              </Button>
            )}
          </div>
          <p className="mb-4 text-sm text-zinc-500">{t('poi.translationHint')}</p>
          {!isFormReady ? (
            <div className="space-y-4 rounded-xl border border-zinc-200 p-4">
              <div className="flex gap-2">
                <Skeleton className="h-7 w-14 rounded-full" />
                <Skeleton className="h-7 w-14 rounded-full" />
                <Skeleton className="h-7 w-14 rounded-full" />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-9 w-full rounded-md" />
                </div>
                <div className="space-y-1.5">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-[124px] w-full rounded-md" />
                </div>
              </div>
            </div>
          ) : (
            <>
              {pendingSuccessJobs.length > 0 && (
                <p className="mb-3 text-sm text-blue-700">
                  {t('jobs.runningCount', {
                    count: pendingSuccessJobs.length,
                  })}
                </p>
              )}
              <PoiTranslationSection
                translations={translations}
                onChange={setTranslations}
                onGenerationStateChange={setHasPendingTranslationGeneration}
                entityId={translationEntityId}
                entityLabel={translations.find((item) => item.name.trim())?.name ?? t('poi.title')}
                initialActiveLanguage={formLanguageCode}
                {...(typeof window !== 'undefined'
                  ? { entityPath: `${window.location.pathname}${window.location.search}` }
                  : {})}
              />
            </>
          )}
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(360px,0.95fr)]">
          {/* Location */}
          <div className="bg-white rounded-2xl border border-zinc-200 p-6 lg:p-7">
            <div className="mb-4 flex items-center justify-between gap-3">
              <SectionHeader title={t('poi.location')} />
              {isEdit && (
                <Button
                  size="sm"
                  disabled={updateMutation.isPending || savingSection !== null || !isLocationDirty}
                  onClick={handleSaveLocation}
                >
                  {savingSection === 'location' && (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  )}
                  {t('common.save')}
                </Button>
              )}
            </div>
            <div className={cn(!isFormReady && 'opacity-50 pointer-events-none')}>
              <PoiMapPicker
                value={location}
                onChange={handleLocationChange}
                destinationAreaBoundary={selectedTenantDestination?.areaBoundary ?? null}
                destinationCenter={selectedTenantDestinationCenter}
              />
            </div>
          </div>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-2xl border border-zinc-200 p-6 lg:p-7">
              <div className="mb-4 flex items-center justify-between gap-3">
                <SectionHeader title={t('poi.basicInfo')} />
                {isEdit && (
                  <Button
                    size="sm"
                    disabled={
                      updateMutation.isPending || savingSection !== null || !isBasicInfoDirty
                    }
                    onClick={handleSaveBasicInfo}
                  >
                    {savingSection === 'basicInfo' && (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    )}
                    {t('common.save')}
                  </Button>
                )}
              </div>
              <div className={cn(!isFormReady && 'opacity-50 pointer-events-none')}>
                <div className="mb-6 space-y-1.5">
                  <label className="text-sm font-medium text-zinc-700">
                    {t('poi.destination')} <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={selectedTenantDestinationId}
                    onValueChange={(nextTenantDestinationId) => {
                      setSelectedTenantDestinationId(nextTenantDestinationId)
                      setBasicInfo((current) => ({ ...current, category: '' }))
                    }}
                    disabled={!tenantId || isTenantDestinationsLoading}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue
                        placeholder={t('poi.selectDestination', {
                          defaultValue: 'Select destination',
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {isTenantDestinationsLoading ? (
                        <div className="flex items-center justify-center gap-2 px-2 py-6 text-sm text-zinc-500">
                          <Loader2 size={14} className="animate-spin" />
                        </div>
                      ) : tenantDestinations.length === 0 ? (
                        <div className="space-y-3 px-3 py-5 text-center">
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-zinc-800">
                              {t('poi.destinationEmptyTitle', {
                                defaultValue: 'No tenant destinations yet',
                              })}
                            </p>
                            <p className="text-xs leading-5 text-zinc-500">
                              {t('poi.destinationEmptyDescription', {
                                defaultValue: 'Create a destination first to link it here.',
                              })}
                            </p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            className="h-8"
                            onClick={handleCreateDestinationNavigation}
                          >
                            <Plus className="mr-1.5 h-3.5 w-3.5" />
                            {t('poi.destinationCreateAction', {
                              defaultValue: 'Create new destination',
                            })}
                          </Button>
                        </div>
                      ) : (
                        tenantDestinations.map((item: ITenantDestination) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.localizedData?.[formLanguageCode]?.name ?? 'No content available'}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {!isTenantDestinationsLoading && tenantDestinations.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 w-full justify-start text-sm font-medium text-blue-600 hover:text-blue-700"
                      onClick={handleCreateDestinationNavigation}
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">
                        {t('poi.destinationCreateAction', {
                          defaultValue: 'Create new destination',
                        })}
                      </span>
                    </Button>
                  )}
                  {selectedTenantDestination && (
                    <p className="text-xs text-zinc-500">
                      {Object.values(selectedTenantDestination.localizedData ?? {})[0]
                        ?.description ??
                        selectedTenantDestination.destinationId ??
                        ''}
                    </p>
                  )}
                </div>
                <PoiBasicInfoForm
                  value={basicInfo}
                  categories={basicInfoCategoryOptions}
                  types={basicInfoTypeOptions}
                  onChange={handleBasicInfoChange}
                  languageCode={formLanguageCode}
                  isCategoriesLoading={poiGlobalCategoriesQuery.isLoading}
                  hasCategoriesNextPage={poiGlobalCategoriesQuery.hasNextPage}
                  isFetchingCategoriesNextPage={poiGlobalCategoriesQuery.isFetchingNextPage}
                  onLoadMoreCategories={() => void poiGlobalCategoriesQuery.fetchNextPage()}
                  onCreateCategory={handleCreateCategoryNavigation}
                />
                {!isEdit && (
                  <div className="mt-6 flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50/60 px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-zinc-800">{t('common.status')}</p>
                      <p className="text-xs text-zinc-500">
                        {isActive ? t('common.active') : t('common.saveAsDraft')}
                      </p>
                    </div>
                    <Switch checked={isActive} onCheckedChange={setIsActive} />
                  </div>
                )}
              </div>
            </div>

            {/* Media */}
            <div className="bg-white rounded-2xl border border-zinc-200 p-6 lg:p-7">
              <div className="mb-4 flex items-center justify-between gap-3">
                <SectionHeader title={t('poi.media.title')} />
                {isEdit && (
                  <Button
                    size="sm"
                    disabled={
                      updateMutation.isPending ||
                      savingSection !== null ||
                      hasUploadingImages ||
                      !isMediaDirty
                    }
                    onClick={handleSaveMedia}
                  >
                    {savingSection === 'media' && (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    )}
                    {t('common.save')}
                  </Button>
                )}
              </div>
              <div className={cn(!isFormReady && 'opacity-50 pointer-events-none')}>
                <p className="mb-3 text-sm font-medium text-zinc-700">
                  {t('poi.media.images')} <span className="text-red-500">*</span>
                </p>
                <PoiImageUpload
                  images={images}
                  onChange={setImages}
                  onFilesSelected={handleSelectedImages}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {unsavedChangesDialog}
    </PageContainer>
  )
}
